import { OnSubscriptionDataOptions } from '@apollo/client'
import { useToggle } from 'ahooks'
import { Avatar, Badge, Button, List, Modal } from 'antd'
import { clone, setWith } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserHoverCard } from '@/components'
import {
  GetUnreadMemberRequestsQuery,
  GroupMemberRequestChangedDocument,
  GroupMemberRequestChangedSubscription,
  useGetUnreadMemberRequestsQuery,
} from '@/generated/graphql'

import { GroupReplyButton } from './group-reply-button.component'

export const GroupApply: React.FC<{ groupId: number }> = ({ groupId }) => {
  const fetchCount = 6
  const { t } = useTranslation()

  const { data, loading, fetchMore } = useGetUnreadMemberRequestsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    variables: {
      groupId,
      pagination: {
        last: fetchCount,
      },
    },
    notifyOnNetworkStatusChange: true,
  })

  const groupMemberRequests =
    data && data.group.unreadMemberRequests
      ? data.group.unreadMemberRequests?.edges
          .slice()
          .reverse()
          .map(({ node }) => node)
      : []
  const pageInfo =
    data && data.group.unreadMemberRequests
      ? data.group.unreadMemberRequests.pageInfo
      : {
          startCursor: null,
          endCursor: null,
          hasPreviousPage: null,
          hasNextPage: null,
        }

  const loadmore = pageInfo.hasPreviousPage && (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Button
        onClick={() =>
          fetchMore({
            variables: {
              pagination: {
                last: fetchCount,
                before: pageInfo?.startCursor,
              },
            },
          })
        }
      >
        {t('groups.grouplist.loadmore')}
      </Button>
    </div>
  )

  const content = (
    <List
      loading={loading}
      footer={loadmore}
      dataSource={groupMemberRequests}
      renderItem={(groupMemberRequest) => (
        <List.Item
          key={groupMemberRequest.requestId}
          extra={<GroupReplyButton requestId={groupMemberRequest.requestId} name={groupMemberRequest.creator.name} />}
        >
          <List.Item.Meta
            avatar={<Avatar src={groupMemberRequest.creator.avatarUrl} />}
            title={
              <UserHoverCard email={groupMemberRequest.creator.email}>{groupMemberRequest.creator.name}</UserHoverCard>
            }
            description={groupMemberRequest.creator.departments.map((department) => department.name).join(', ')}
          />
        </List.Item>
      )}
    />
  )
  return content
}

export const GroupApplyModal: React.FC<{ groupId: number }> = ({ children, groupId }) => {
  const { t } = useTranslation()
  const [visible, { toggle }] = useToggle()
  const { data, subscribeToMore } = useGetUnreadMemberRequestsQuery({
    variables: {
      groupId,
      pagination: {
        last: 1,
      },
    },
  })

  React.useEffect(() => {
    ;(subscribeToMore as any)({
      document: GroupMemberRequestChangedDocument,
      variables: {
        groupId,
      },
      updateQuery: (
        prev: GetUnreadMemberRequestsQuery,
        { subscriptionData }: OnSubscriptionDataOptions<GroupMemberRequestChangedSubscription>
      ) =>
        setWith(
          clone(prev),
          ['group', 'unreadMemberRequests', 'totalCount'],
          subscriptionData.data?.groupMemeberRequestChanged.unreadMemberRequests.totalCount,
          clone
        ),
    })
  }, [])

  const totalCount = data ? data.group.unreadMemberRequests.totalCount : 0

  children = React.cloneElement(children as any, {
    onClick: () => toggle(),
  })

  return (
    <>
      <Badge dot count={totalCount}>
        {children}
      </Badge>
      <Modal
        title={t('group.applyList.applyList')}
        visible={visible}
        footer={null}
        onCancel={() => toggle()}
        destroyOnClose
        bodyStyle={{ paddingTop: 0 }}
      >
        <GroupApply groupId={groupId} />
      </Modal>
    </>
  )
}
